import { useState } from 'react';

import classNames from 'classnames';
import Fade from '@mui/material/Fade';
import Skeleton from '@mui/material/Skeleton';

import { Box } from '@vk-hr-tek/ui/Box';
import { Link } from '@vk-hr-tek/ui/Link';
import { Typography, TypographyColor } from '@vk-hr-tek/ui/Typography';
import { Icon } from '@vk-hr-tek/ui/Icon';
import { useIsDesktop, useTextOverflow } from '@vk-hr-tek/ui/hooks';
import { Tooltip } from '@vk-hr-tek/ui/Tooltip';

import { usePageProps } from '@app/context';
import { useRemoveDomain, useSelector } from '@app/hooks';

import { selectStatus } from '../../../../user';
import { useUrlWithPrefix } from '../../../hooks';
import { TIMEOUT_FOR_FADE } from '../../../constants';
import { IconNameValues } from '../../../types';
import { useSidebarContext } from '../SidebarContext';

import { useStyles } from './SidebarItem.styles';

interface SidebarItemProps {
  title: string;
  color?: TypographyColor;
  iconName: IconNameValues;
  href: string;
  isActive: boolean;
  aqaClass: string;
  external?: boolean;
  blank?: boolean;
  isCustom?: boolean;
  showIcon?: boolean;
  isInDoubleDrop?: boolean;
}

export const SidebarItem = ({
  title,
  color,
  href,
  aqaClass,
  iconName,
  isActive,
  external = false,
  blank = false,
  isCustom = false,
  showIcon = true,
  isInDoubleDrop,
}: SidebarItemProps) => {
  const { classes } = useStyles();
  const isDesktop = useIsDesktop();
  const { sidebarOpened, onMenuItemClick } = useSidebarContext();
  const isSkeletonLoading = useSelector(selectStatus) === 'loading';

  const { ref, isOverflowed, checkOverflow } = useTextOverflow();
  const [hasSidebarClosed, setHasSidebarClosed] = useState(!sidebarOpened);

  const pageProps = usePageProps();

  const onExternalItemClick =
    pageProps && 'onItemClick' in pageProps ? pageProps.onItemClick : undefined;

  const urlWithPrefix = useUrlWithPrefix(external);

  const nativeLinkParams =
    external && onExternalItemClick ? {} : urlWithPrefix(href);

  const hrefWithoutDomain = useRemoveDomain(href);

  const handleClickItem = () => {
    onExternalItemClick?.(hrefWithoutDomain);
  };

  const onCustomAction = () => {
    if (!isActive) {
      onMenuItemClick(title);
    }
  };

  const isInDropAndClosedSidebar = !sidebarOpened && !showIcon;
  const isTooltipVisible =
    !isInDropAndClosedSidebar && (isOverflowed || !sidebarOpened);
  const isTextWrapped = hasSidebarClosed && isInDropAndClosedSidebar;

  const renderIcon = () => (
    <Box
      p="8"
      display="flex"
      visibility={showIcon ? 'visible' : 'hidden'}
      minWidth={40}
      minHeight={40}
      alignItems="center"
      justifyContent="center"
    >
      {isSkeletonLoading ? (
        <Skeleton width="100%" />
      ) : (
        <Icon
          name={iconName}
          size="20"
          color={
            iconName === 'sliders_outline'
              ? 'original.brand.primary'
              : 'text.light.primary'
          }
        />
      )}
    </Box>
  );

  const renderTitle = () => (
    <Fade
      in={!showIcon ? !showIcon : sidebarOpened}
      timeout={TIMEOUT_FOR_FADE}
      appear={false}
      onExited={() => setHasSidebarClosed(true)}
      onEnter={() => setHasSidebarClosed(false)}
    >
      <Box
        minWidth={0}
        {...(isInDropAndClosedSidebar && { px: '16', py: '12' })}
        pl={isInDoubleDrop && sidebarOpened ? '24' : undefined}
        pr="8"
      >
        <Typography
          ref={ref}
          variant="body3"
          color={color ?? 'text.light.primary'}
          noWrap={!isTextWrapped}
        >
          {isSkeletonLoading ? <Skeleton width={200} /> : title}
        </Typography>
      </Box>
    </Fade>
  );

  const content = (
    <Tooltip
      maxWidth="310px"
      title={isTooltipVisible ? title : ''}
      placement="right"
      disabled={!isDesktop}
    >
      <Box
        className={classNames(
          classes.menuItem,
          isActive && classes.active,
          aqaClass,
        )}
        onMouseEnter={!isInDropAndClosedSidebar ? checkOverflow : undefined}
      >
        {!isInDropAndClosedSidebar && renderIcon()}
        {renderTitle()}
      </Box>
    </Tooltip>
  );

  if (isCustom) {
    return (
      <Box>
        <a
          href={href}
          className={classes.link}
          target={blank ? '_blank' : '_self'}
          rel="noopener noreferrer"
        >
          {content}
        </a>
      </Box>
    );
  }

  return (
    <Box>
      <Link
        {...nativeLinkParams}
        onClick={handleClickItem}
        onCustomAction={onCustomAction}
        variant="simple"
      >
        {content}
      </Link>
    </Box>
  );
};
